import type {
  Canvas,
  FileRecord,
  InboxItem,
  Skill,
} from "@cohesive/api-client";

export type HomeTab = "all" | "recents" | "canvases" | "chats" | "starred";
export type HomeViewMode = "grid" | "list";

export type HomeSortField = "name" | "type" | "size" | "modified";
export type HomeSortDir = "asc" | "desc";

/** Column-driven sort: a field plus direction, like the old workspace detail
 *  list view. The list column headers toggle direction on the active field;
 *  the header SortMenu (grid view's only sort control) picks a field at its
 *  natural default direction. */
export interface HomeSort {
  field: HomeSortField;
  dir: HomeSortDir;
}

/** Each field's natural default direction: text ascending, freshest/biggest
 *  first for dates and sizes. Switching to a field starts here; clicking the
 *  already-active column header flips it. */
export const HOME_SORT_DEFAULT_DIR: Record<HomeSortField, HomeSortDir> = {
  name: "asc",
  type: "asc",
  size: "desc",
  modified: "desc",
};

export function naturalHomeSort(field: HomeSortField): HomeSort {
  return { field, dir: HOME_SORT_DEFAULT_DIR[field] };
}

/** Tab order in the header strip. */
export const HOME_TABS: readonly HomeTab[] = [
  "all",
  "recents",
  "canvases",
  "chats",
  "starred",
];

/** Resource-heavy tabs lead on thumbnails; chats read better as rows. */
export const TAB_DEFAULT_VIEW: Record<HomeTab, HomeViewMode> = {
  all: "grid",
  recents: "grid",
  canvases: "grid",
  chats: "list",
  starred: "grid",
};

/** Page cap for the org-wide Recents / Canvases / Chats / Starred queries. */
export const HOME_BROWSER_LIMIT = 50;

/**
 * One entry in the browser list. A discriminated union so per-kind rendering
 * and actions plug in at one seam; a future kind is a new arm + adapter case.
 */
export type BrowserItem =
  | { kind: "canvas"; data: Canvas }
  | { kind: "file"; data: FileRecord }
  | { kind: "skill"; data: Skill }
  | { kind: "chat"; data: InboxItem };

export type BrowserItemKind = BrowserItem["kind"];

export function browserItemId(item: BrowserItem): string {
  return `${item.kind}:${item.data.id}`;
}

/**
 * Workspace-selection id for an item, matching the `SelectableItem` ids the
 * card hooks register (`folder:` for folder file-records, unlike
 * `browserItemId`). Skills don't participate in selection — null.
 */
export function browserItemSelectionId(item: BrowserItem): string | null {
  switch (item.kind) {
    case "canvas":
      return `canvas:${item.data.id}`;
    case "file":
      return item.data.path.endsWith("/")
        ? `folder:${item.data.id}`
        : `file:${item.data.id}`;
    case "chat":
      return `chat:${item.data.id}`;
    case "skill":
      return null;
  }
}
